import { useEffect, useRef, useState } from 'react'

/** Minimum time skeleton stays visible so fast API responses don't flash loading UI. */
export const INBOX_SKELETON_MIN_MS = 900

/**
 * Keeps skeleton visible for at least `minMs` after loading starts, even if `isLoading`
 * flips false immediately. Prevents jarring flicker on quick refetches.
 */
export function useMinSkeletonDuration(isLoading: boolean, minMs = INBOX_SKELETON_MIN_MS): boolean {
    const [visible, setVisible] = useState(isLoading)
    const loadingStartedAtRef = useRef<number | null>(null)
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (isLoading) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
            loadingStartedAtRef.current = Date.now()
            setVisible(true)
            return
        }

        if (loadingStartedAtRef.current == null) {
            setVisible(false)
            return
        }

        const elapsed = Date.now() - loadingStartedAtRef.current
        loadingStartedAtRef.current = null
        const remaining = Math.max(0, minMs - elapsed)

        if (remaining === 0) {
            setVisible(false)
            return
        }

        timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null
            setVisible(false)
        }, remaining)

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }
    }, [isLoading, minMs])

    return visible
}
